import type { ReauthPurpose, StepUpChallenge, UserIdentitySummary } from '@/api';

/**
 * The subset of the API client the Connected Accounts UI needs. `src/api`
 * exposes these through the `RustyConnectedAccounts` wiring, so a page just
 * passes its `api` through.
 */
export interface ConnectedAccountsApi {
  listIdentities(): Promise<UserIdentitySummary[]>;
  linkOidcInitiate(): Promise<{ auth_url: string }>;
  reauthInitiate(purpose: ReauthPurpose): Promise<StepUpChallenge>;
  reauthNostrVerify(
    purpose: ReauthPurpose,
    signedEvent: unknown
  ): Promise<{ step_up_token: string }>;
  linkCommit(proofToken: string, stepUpToken: string): Promise<UserIdentitySummary>;
  unlinkIdentity(id: string, stepUpToken: string): Promise<{ success: true }>;
  deleteAccount(confirmation: string, stepUpToken: string): Promise<{ deleted: boolean }>;
}

/**
 * Persisted across the redirect legs of a link/unlink/delete flow. `proof` is
 * only present for the link flow (minted by the OIDC link round-trip). `action`
 * encodes the in-flight operation: `"link"`, `"unlink:<identityId>"`, or
 * `"delete"`.
 */
export interface PendingFlow {
  action: 'link' | `unlink:${string}` | 'delete';
  proof?: string;
}

interface StoredPendingFlow extends PendingFlow {
  createdAt: number;
}

export const FLOW_STORAGE_KEY = 'connectedAccounts.pendingFlow';

/**
 * How long a pending flow stays resumable. The server-side proofs expire after
 * 5 minutes; the slack here just means a late click fails with the backend's
 * `step_up_invalid` instead of a confusing "no pending flow".
 */
const FLOW_TTL_MS = 15 * 60 * 1000;

/**
 * `localStorage`, not `sessionStorage`: the email step-up leg lands via a link
 * in the user's mail client, which opens a NEW tab — and `sessionStorage` is
 * per-tab, so the flow would be lost exactly when it's needed. The auth session
 * lives in `localStorage` for the same reason.
 */
export function readPendingFlow(): PendingFlow | null {
  try {
    const raw = localStorage.getItem(FLOW_STORAGE_KEY);
    if (!raw) return null;
    const stored = JSON.parse(raw) as StoredPendingFlow;
    // A record with no usable `createdAt` is treated as expired, not as valid:
    // failing open here would give any planted or malformed record an unbounded
    // lifetime, which is exactly what the TTL exists to prevent.
    if (!stored.action || !Number.isFinite(stored.createdAt)) {
      clearPendingFlow();
      return null;
    }
    if (Date.now() - stored.createdAt > FLOW_TTL_MS) {
      clearPendingFlow();
      return null;
    }
    return { action: stored.action, proof: stored.proof };
  } catch {
    return null;
  }
}

export function writePendingFlow(flow: PendingFlow): void {
  try {
    const stored: StoredPendingFlow = { ...flow, createdAt: Date.now() };
    localStorage.setItem(FLOW_STORAGE_KEY, JSON.stringify(stored));
  } catch {
    // Storage unavailable (private mode / quota) — the in-place nostr path still
    // works; redirect legs will surface `no_pending_flow` as before.
  }
}

export function clearPendingFlow(): void {
  try {
    localStorage.removeItem(FLOW_STORAGE_KEY);
    // Drop any record left by the previous sessionStorage-based build.
    sessionStorage.removeItem(FLOW_STORAGE_KEY);
  } catch {
    // ignore
  }
}
